import { DEFER_COLLAB_SEND } from '@atlaskit/editor-common/collab-defer-collab-send';
import { STREAMING_COLLAPSIBLE_STEPS } from '@atlaskit/editor-common/collab-streaming-step-collapse';
import { Slice, type Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import {
	TextSelection,
	type EditorState,
	type Transaction,
} from '@atlaskit/editor-prosemirror/state';

import { countCompleteBlocks } from '../../../ui/utils/complete-blocks';
import { resolveAgentAttribution } from '../../agent-attribution/resolveAgentAttribution';
import { aiSessionContextPluginKey } from '../../ai-session-context/ai-session-context-plugin-key';
import {
	CAPTURE_ABORTED_TOOL_CALL_POSITIONS,
	CELL_KIND_CONVERSION_RESULT,
} from '../../ai-session-context/meta-keys';
import { aiExperienceDecorationPluginKey } from '../../decoration/decoration-plugin-key';
import { purePmInEditorDirectStreamingPluginKey } from '../pluginKey';
import { ACTIONS, metaTransaction, updateStreamPositions } from '../transforms';
import type { PluginMeta } from '../types';
import { calculateTelepointerPos } from '../utils/adfConversion';
import type { CellKindConversionRejection, CellKindConversionResult } from './cellKindConversion';
import { stampUnifiedAgentEditChrome } from './stampUnifiedAgentEditChrome';

/** Locates the wrapper to retain before any streaming frame changes it. */
export function getEnclosingCell(
	doc: PMNode,
	pos: number,
): { node: PMNode; pos: number } | undefined {
	if (!Number.isInteger(pos) || pos < 0 || pos > doc.content.size) {
		return undefined;
	}
	const directNode = doc.nodeAt(pos);
	if (
		directNode?.type.spec.tableRole === 'cell' ||
		directNode?.type.spec.tableRole === 'header_cell'
	) {
		return { node: directNode, pos };
	}
	const $pos = doc.resolve(pos);
	for (let depth = $pos.depth; depth > 0; depth--) {
		const node = $pos.node(depth);
		if (node.type.spec.tableRole === 'cell' || node.type.spec.tableRole === 'header_cell') {
			return { node, pos: $pos.before(depth) };
		}
	}
	return undefined;
}

/** Restore the closed wrapper, rather than fitting its content into the new kind. */
export function restoreOriginalCell(state: EditorState): Transaction | undefined {
	const streamingState = purePmInEditorDirectStreamingPluginKey.getState(state);
	const originalCell = streamingState?.cellKindConversion?.hasApplied
		? streamingState.cellKindConversion.originalCell
		: undefined;
	const startPos = streamingState?.startPos;
	if (!originalCell || startPos === undefined) {
		return undefined;
	}
	const cell = getEnclosingCell(state.doc, startPos);
	if (!cell || cell.node.attrs.localId !== originalCell.attrs.localId) {
		return undefined;
	}
	let tr = state.tr;
	if (!cell.node.eq(originalCell)) {
		tr = tr.replaceWith(cell.pos, cell.pos + cell.node.nodeSize, originalCell);
	}
	const restoredStart = cell.pos + 1;
	const restoredEnd = restoredStart + originalCell.content.size;
	return updateStreamPositions({
		startPos: restoredStart,
		userContentEndPos: restoredEnd,
		aiContentEndPos: restoredEnd,
		telepointerPos: calculateTelepointerPos(tr.doc, restoredStart, restoredEnd),
		isFinalStream: false,
	})(tr);
}

/** Applies the validated wrapper as one edit and tracks its exact new inner range. */
export function buildCellKindConversionTransaction(
	state: EditorState,
	conversion: Extract<CellKindConversionResult, { status: 'ready' }>,
	isFinalStream: boolean,
): Transaction {
	const streamingState = purePmInEditorDirectStreamingPluginKey.getState(state);
	const sessionState = aiSessionContextPluginKey.getState(state);
	const decorationState = aiExperienceDecorationPluginKey.getState(state);
	const { cellPos, cellEnd, replacementCell } = conversion;
	let tr = state.tr.replaceWith(cellPos, cellEnd, replacementCell);
	const startPos = cellPos + 1;
	const endPos = startPos + replacementCell.content.size;

	if (isFinalStream) {
		tr.setMeta(DEFER_COLLAB_SEND, false);
		tr.setMeta(CELL_KIND_CONVERSION_RESULT, { rejection: undefined });
		tr.setSelection(TextSelection.near(tr.doc.resolve(endPos), -1));
		if (state.doc.eq(tr.doc)) {
			tr.setMeta('addToHistory', false);
		}
	} else {
		tr.setMeta('ignoreLocalIdUpdate', true);
		tr.setMeta('addToHistory', false);
	}

	const collapseSteps = !isFinalStream;
	const completeBlocks = collapseSteps
		? countCompleteBlocks({
				adfResponse: sessionState?.adfResponse,
				markdown: sessionState?.markdown,
			})
		: 0;
	const flushCollabSteps = completeBlocks > (streamingState?.collabFlushedBlockCount ?? 0);
	if (collapseSteps) {
		tr.setMeta(STREAMING_COLLAPSIBLE_STEPS, true);
		tr.setMeta(DEFER_COLLAB_SEND, !flushCollabSteps);
	}
	tr = metaTransaction({
		tr,
		isFinalStream,
		stackTime: streamingState?.streamTimeStamp,
		agentAttribution: resolveAgentAttribution({
			sessionAgentAttribution: sessionState?.agentAttribution,
			configItemKey: decorationState?.configItem?.key,
		}),
	});
	tr = updateStreamPositions({
		startPos,
		userContentEndPos: endPos,
		aiContentEndPos: endPos,
		telepointerPos: calculateTelepointerPos(tr.doc, startPos, endPos),
		isFinalStream,
		lastProcessedVersion: sessionState?.responseVersion,
		aiContentSlice: new Slice(replacementCell.content, 0, 0),
		scrollTargetPos: endPos,
		posAdjusted: true,
		cellKindConversion: {
			originalCell: conversion.originalCell,
			originalRangeIsFull: true,
			hasApplied: true,
			status: isFinalStream ? (state.doc.eq(tr.doc) ? 'unchanged' : 'committed') : 'streaming',
		},
		...(flushCollabSteps && { collabFlushedBlockCount: completeBlocks }),
	})(tr);
	if (isFinalStream && sessionState?.pendingForceAbortedToolCallId) {
		tr.setMeta(CAPTURE_ABORTED_TOOL_CALL_POSITIONS, {
			toolCallId: sessionState.pendingForceAbortedToolCallId,
			startPos,
			aiContentEndPos: endPos,
		});
	}
	return isFinalStream ? stampUnifiedAgentEditChrome(tr, state) : tr;
}

/** Removes intermediate conversion frames and records the terminal rejection. */
export function buildCellKindRejectionTransaction(
	state: EditorState,
	rejection: CellKindConversionRejection,
	isFinalStream: boolean,
): Transaction {
	const streamingState = purePmInEditorDirectStreamingPluginKey.getState(state);
	const sessionState = aiSessionContextPluginKey.getState(state);
	let tr = restoreOriginalCell(state) ?? state.tr;
	const restoreMeta = tr.getMeta(purePmInEditorDirectStreamingPluginKey) as PluginMeta | undefined;
	const restoredPositions =
		restoreMeta?.action === ACTIONS.UPDATE_STREAM_POSITIONS ? restoreMeta.positions : undefined;
	tr = updateStreamPositions({
		startPos: restoredPositions?.startPos ?? streamingState?.startPos,
		userContentEndPos: restoredPositions?.userContentEndPos ?? streamingState?.userContentEndPos,
		aiContentEndPos: restoredPositions?.aiContentEndPos ?? streamingState?.startPos ?? 0,
		telepointerPos: restoredPositions?.telepointerPos ?? streamingState?.startPos ?? 0,
		isFinalStream,
		lastProcessedVersion: sessionState?.responseVersion,
		...(streamingState?.cellKindConversion && {
			cellKindConversion: {
				...streamingState.cellKindConversion,
				originalRangeIsFull:
					streamingState.cellKindConversion.originalRangeIsFull &&
					rejection.reason !== 'partialCellRange',
				status: 'rejected' as const,
			},
		}),
	})(tr);
	tr.setMeta('addToHistory', false);
	tr.setMeta('isAIRestoreTransaction', tr.docChanged);
	tr = metaTransaction({
		tr,
		isFinalStream,
		stackTime: streamingState?.streamTimeStamp,
		agentAttribution: resolveAgentAttribution({
			sessionAgentAttribution: sessionState?.agentAttribution,
			configItemKey: aiExperienceDecorationPluginKey.getState(state)?.configItem?.key,
		}),
	});
	if (isFinalStream) {
		tr.setMeta(DEFER_COLLAB_SEND, false);
		tr.setMeta(CELL_KIND_CONVERSION_RESULT, { rejection });
	}
	return tr;
}
